import { useState } from "react";
import { Link } from "react-router-dom";
import { testimonials } from "@/mocks/homeData";
import { portfolioProjects, PortfolioProject } from "@/mocks/portfolioData";
import ProjectRequestForm from "./components/ProjectRequestForm";
import ProjectModal from "./components/ProjectModal";

const categories = ["All", "AI & Analytics", "App Development", "Digital Marketing", "Cybersecurity", "Big Data", "IT Staffing"];

export default function PortfolioPage() {
  const [active, setActive] = useState("All");
  const [selectedProject, setSelectedProject] = useState<PortfolioProject | null>(null);
  const filtered = active === "All" ? portfolioProjects : portfolioProjects.filter((p) => p.category === active);

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-20 bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20creative%20portfolio%20technology%20dark%20background%20geometric%20shapes%20glowing%20minimal%20cinematic%20wide&width=1440&height=500&seq=porthero&orientation=landscape"
            alt="Portfolio background"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/98 to-black/75" />
        </div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C8F135]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#C8F135]/10 border border-[#C8F135]/20 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[#C8F135] text-xs font-semibold uppercase tracking-wider">Our Work</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-5">
            Case Studies &amp; <span style={{color: "#C8F135"}}>Portfolio</span>
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Real projects. Real results. Explore how we&apos;ve helped 500+ businesses transform their digital operations.
          </p>
        </div>
      </section>

      {/* Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setActive(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  active === cat ? "bg-black text-[#C8F135]" : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}>
                {cat}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((project) => (
              <button
                key={project.id}
                onClick={() => setSelectedProject(project)}
                className="group rounded-xl overflow-hidden border border-slate-100 cursor-pointer hover:-translate-y-1 transition-transform duration-300 text-left w-full"
              >
                <div className="relative w-full h-52 overflow-hidden">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-3 left-3">
                    <span className="bg-white text-slate-700 text-xs font-semibold px-3 py-1 rounded-full">{project.category}</span>
                  </div>
                  <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#C8F135] text-black">
                      <i className="ri-arrow-right-up-line text-sm" />
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-white">
                  <h3 className="text-base font-bold text-slate-900 mb-2">{project.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span key={tag} className="text-xs bg-slate-50 text-slate-600 border border-slate-100 px-2.5 py-1 rounded-md">{tag}</span>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold" style={{color: "#9bc400"}}>
                    <span>View Case Study</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs" />
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              What Our <span style={{color: "#9bc400"}}>Clients Say</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.slice(0, 3).map((t) => (
              <div key={t.id} className="bg-white rounded-xl p-7 border border-slate-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center text-sm font-black text-white bg-slate-800 flex-shrink-0">
                    {t.avatar}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.title}</div>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => <i key={i} className="ri-star-fill text-[#C8F135] text-sm" />)}
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">&ldquo;{t.text}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProjectRequestForm />

      <section className="py-20 bg-black">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Ready to be our next success story?</h2>
          <p className="text-slate-400 mb-8">Let&apos;s discuss your project and build something extraordinary together.</p>
          <Link to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#C8F135] text-black font-bold text-base whitespace-nowrap cursor-pointer hover:bg-[#b5e000] transition-colors">
            Start Your Project <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
    </main>
  );
}
